// Central content + mock data for VerifiAssist marketing site.
// NOTE: Contact form submissions are stored in localStorage (mock) until backend is wired.

export const company = {
  name: "VerifiAssist",
  legal: "VerifiAssist World Wide LLC",
  phone: "(980) 407-5508",
  phoneRaw: "9804075508",
  email: "nick@verifiassist.com",
  logo: "/assets/logo.png",
  tagline: "Stop Revenue Loss from Insurance Mistakes",
  socials: {
    facebook: "https://www.facebook.com/profile.php?id=61577239241885",
    instagram: "https://www.instagram.com/verifiassist/",
    linkedin: "https://www.linkedin.com/company/verifiassist/",
  },
};

export const images = {
  heroAgent: "https://images.pexels.com/photos/19957213/pexels-photo-19957213.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  dentalReception: "https://images.pexels.com/photos/6812426/pexels-photo-6812426.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  clinicConsult: "https://images.pexels.com/photos/6809658/pexels-photo-6809658.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  proLaptop: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  femaleDoctor: "https://images.pexels.com/photos/5214963/pexels-photo-5214963.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  dentalClinic: "https://images.pexels.com/photos/6812453/pexels-photo-6812453.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  orthoPatient: "https://images.unsplash.com/photo-1667133295352-ef4c83620e8e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  bracesExam: "https://images.pexels.com/photos/6528907/pexels-photo-6528907.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  hospitalCorridor: "https://images.pexels.com/photos/6129450/pexels-photo-6129450.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  hospitalTeam: "https://images.pexels.com/photos/31499386/pexels-photo-31499386.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  businessTeam: "https://images.pexels.com/photos/7693692/pexels-photo-7693692.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  businessTeam2: "https://images.unsplash.com/photo-1622675363311-3e1904dc1885?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

export const nav = {
  primary: [
    { label: "Home", to: "/" },
    {
      label: "Solutions",
      children: [
        { label: "Insurance Verification", to: "/insurance-verification", desc: "Verify eligibility & benefits before patients arrive" },
        { label: "Dental & Orthodontics", to: "/dental-orthodontics", desc: "Save time & money on verification, billing & admin" },
        { label: "For Hospitals & Clinics", to: "/for-hospitals-clinics", desc: "24/7 HIPAA-compliant healthcare call center" },
        { label: "For Insurance Providers", to: "/for-insurance-providers", desc: "End-to-end claims cycle management" },
        { label: "HIPAA Privacy Policies", to: "/hipaa-privacy-policies", desc: "How we protect patient data" },
      ],
    },
    { label: "AR & Debt Collections", to: "/ar-debt-collections" },
    { label: "Pricing", to: "/pricing" },
    { label: "Contact Us", to: "/contact-us" },
  ],
};

export const home = {
  hero: {
    eyebrow: "Remote Revenue Cycle & Insurance Support",
    title: "Stop Revenue Loss from Insurance Mistakes",
    subtitle:
      "VerifiAssist gives dental, orthodontic and medical practices dedicated remote agents for insurance verification, claims follow-up and billing — so you get paid faster without adding payroll.",
    ctaPrimary: { label: "Get a Free Revenue Risk Audit", to: "/contact-us" },
    ctaSecondary: { label: "See What Practices Save", to: "/dental-orthodontics" },
    stats: [
      { value: "10+ hrs", label: "Saved per provider / week" },
      { value: "30%", label: "Fewer insurance errors" },
      { value: "40%", label: "Higher staff productivity" },
      { value: "20–40%", label: "Aging A/R recovered" },
    ],
  },
  trustLogos: ["General Dentistry", "Orthodontics", "DSOs", "Medical Practices", "Hospitals", "Clinics"],
  valueProps: [
    {
      icon: "ShieldCheck",
      title: "Reduce Denials & Maximize Reimbursements",
      body: "We verify eligibility, benefits, frequency limits and deductibles upfront so your practice gets paid faster and avoids costly claim rejections.",
    },
    {
      icon: "ClipboardCheck",
      title: "Prior Authorizations Handled",
      body: "We secure required authorizations before treatment begins to prevent denials, protect production and avoid scheduling disruptions.",
    },
    {
      icon: "Users",
      title: "Patient Insurance Support",
      body: "Clear benefit breakdowns before appointments — deductibles, co-pays and coverage limits — so patients keep appointments and collections improve.",
    },
    {
      icon: "TrendingUp",
      title: "Claims Follow-Up & A/R",
      body: "Proactive tracking of outstanding claims so reimbursements don't stall, aging A/R stays controlled and cash flow stays predictable.",
    },
  ],
  mission: {
    title: "Built to protect the revenue you've already earned",
    body: [
      "VerifiAssist was built around a simple observation: most practices don't lose revenue from major billing failures — they lose it through small, inconsistent insurance processes that go unnoticed.",
      "Verification gaps. Unfollowed claims. Missed authorizations. Overloaded front desks. Over time, these small breakdowns create denial risk, delayed reimbursements and preventable write-offs.",
      "Our approach is process-driven — not task-based. We align with your existing systems, standardize workflows and reduce variability so collections become predictable.",
    ],
    bullets: [
      "Insurance verification & denial prevention",
      "Claims filing & proactive follow-up",
      "Prior authorization management",
      "Patient insurance breakdown support",
      "Revenue-focused administrative operations",
    ],
  },
  industries: {
    title: "Industries We Serve",
    subtitle: "Structured remote support tailored to how your organization actually operates.",
    groups: [
      {
        icon: "Stethoscope",
        title: "Dental Practices & Orthodontics",
        items: [
          "General Dentistry — reduce write-offs & denials before patients arrive",
          "Orthodontic Practices — protect case acceptance & prevent treatment delays",
          "DSOs / Multi-Location — standardize insurance workflows across every location",
          "Medical Practices — reduce claim rejections & accelerate reimbursements",
        ],
      },
      {
        icon: "Wrench",
        title: "Service-Based Businesses",
        items: [
          "HVAC, Plumbing & Electrical — collect on completed work",
          "Contractors & Trades — recover revenue tied up in open balances",
          "Auto Repair & Mechanics — turn completed work into collected cash",
          "Recurring Service Businesses — eliminate missed follow-ups",
        ],
      },
      {
        icon: "Briefcase",
        title: "Professional Services",
        items: [
          "Marketing Agencies — collect on retainers & completed work",
          "Consultants — turn outstanding invoices into cash",
          "IT Service Providers (MSPs) — improve monthly collection consistency",
          "Accounting & Bookkeeping — steady cash flow with managed follow-up",
        ],
      },
    ],
  },
  steps: {
    title: "How VerifiAssist Works",
    subtitle: "A structured onboarding that plugs into your existing systems in days, not months.",
    items: [
      { n: "01", title: "Revenue Risk Audit", body: "We review your verification and claims workflows to pinpoint where revenue is slipping through the cracks." },
      { n: "02", title: "Workflow Standardization", body: "We build consistent, HIPAA-conscious processes aligned to your PMS and front-desk operations." },
      { n: "03", title: "Dedicated Remote Agents", body: "Trained agents verify benefits, secure authorizations and follow up on claims as an extension of your team." },
      { n: "04", title: "Transparent Reporting", body: "You get clear reporting on verifications, denials prevented and A/R recovered — full visibility, always." },
    ],
  },
  testimonials: [
    {
      quote:
        "VerifiAssist took verification off our front desk entirely. Denials dropped and our team finally has time for patients.",
      name: "Practice Manager",
      role: "3-location Dental Group",
    },
    {
      quote:
        "Our aging A/R was out of control. Within a quarter they recovered balances we'd written off as lost.",
      name: "Office Administrator",
      role: "Orthodontic Practice",
    },
    {
      quote:
        "It's like adding an experienced insurance department without adding payroll. The reporting alone is worth it.",
      name: "Owner Dentist",
      role: "General Dentistry",
    },
  ],
  faqs: [
    { q: "Do you work inside our practice management software?", a: "Yes. We work as an extension of your team inside your existing PMS and clearinghouse — no new software to buy." },
    { q: "Is VerifiAssist HIPAA compliant?", a: "We follow HIPAA-conscious workflows and secure data-handling processes on every verification and interaction." },
    { q: "How fast can we get started?", a: "Most practices are onboarded within days. We start with a free revenue risk audit, then standardize your workflows." },
    { q: "Do we have to hire or manage anyone?", a: "No. You get dedicated remote agents and transparent reporting without the cost of hiring, training or retaining in-house staff." },
  ],
};

export const insuranceVerification = {
  hero: {
    eyebrow: "Healthcare Call Center",
    title: "Reduce Denials and Get Paid Faster with Insurance Verification",
    subtitle:
      "Accurate insurance verification helps your team prevent denials, reduce front-desk overload and protect cash flow — before patients arrive.",
    image: "clinicConsult",
  },
  intro: [
    "At VerifiAssist, we provide structured insurance verification support that helps practices reduce claim denials, improve reimbursement speed and free up internal staff from time-consuming payer follow-up. Our team verifies eligibility, benefits, coverage details and key plan requirements before patients arrive.",
    "We work as an extension of your team using secure, HIPAA-conscious workflows built for consistency, accuracy and reliability. Instead of overloading your staff with manual verification tasks, we help you create a cleaner process that supports smoother operations and more predictable payments.",
  ],
  benefits: [
    { icon: "Gauge", title: "Reduced Administrative Burden", body: "Free your internal staff to focus on patient care instead of spending hours on eligibility verification, while HIPAA compliance is maintained." },
    { icon: "Target", title: "Improved Accuracy & Efficiency", body: "HIPAA-certified agents navigate complex insurance policies to verify eligibility quickly and accurately — faster reimbursement, fewer denials." },
    { icon: "BadgeCheck", title: "Quality Assurance", body: "A rigorous QA process ensures all calls, emails and eFax interactions comply with secure verification standards." },
    { icon: "Scaling", title: "Scalable Solutions", body: "We easily adjust team size and services to meet your changing needs — from a single provider to multi-location groups." },
    { icon: "FileBarChart", title: "Transparent Reporting", body: "Detailed performance reporting so you can see exactly how verification and denials are trending at all times." },
    { icon: "PiggyBank", title: "Cost Savings", body: "More cost-effective than hiring, training and retaining specialized in-house verification staff." },
    { icon: "HeartHandshake", title: "Improved Patient Satisfaction", body: "Pre-visit verification gives patients clarity on financial responsibility, reducing uncertainty and no-shows." },
    { icon: "RefreshCw", title: "Streamlined Revenue Cycle", body: "Accurate verification means claims process correctly the first time — faster reimbursements and improved cash flow." },
  ],
  security: {
    title: "Security & Compliance You Can Trust",
    body: [
      "Handling patient and insurance data requires accuracy, consistency and strict attention to security. Our team follows HIPAA-conscious workflows designed to protect sensitive information while ensuring every verification is completed thoroughly.",
      "Instead of relying on overloaded front-desk staff, we provide a structured process that reduces errors and supports a more predictable revenue cycle. All communication and data-handling processes are built with security in mind, so you can confidently outsource without adding risk.",
    ],
  },
};

export const dental = {
  hero: {
    eyebrow: "For Dental & Orthodontic Practices",
    title: "Save Time and Money on Insurance Verification, Billing & Admin",
    subtitle:
      "Give your orthodontic or dental practice a dedicated remote team for insurance verification, claims, billing and front-desk admin — so your staff can focus on patients and case acceptance, not paperwork.",
    image: "orthoPatient",
  },
  problem: {
    title: "Why practices quietly lose revenue",
    body: "Dental and orthodontic practices rarely lose money from big billing failures. They lose it through small, repeated breakdowns — an unverified benefit, a missed frequency limitation, an authorization that never got submitted, a claim nobody followed up on. Multiply that across every chair, every day.",
    pains: [
      { icon: "AlertTriangle", title: "Unverified benefits", body: "Inaccurate breakdowns lead to surprise balances, disputes and write-offs." },
      { icon: "Clock", title: "Overloaded front desk", body: "Staff juggle phones, patients and payer holds — details get missed." },
      { icon: "FileX", title: "Preventable denials", body: "Missed frequency limits, downgrades and deductibles trigger rejections." },
      { icon: "TrendingDown", title: "Aging A/R", body: "Unfollowed claims sit in receivables and slowly become write-offs." },
    ],
  },
  savings: {
    title: "What practices save with VerifiAssist",
    items: [
      { value: "10+ hrs", label: "Front-desk hours saved per provider each week", icon: "Clock" },
      { value: "30%", label: "Reduction in preventable insurance errors", icon: "ShieldCheck" },
      { value: "40%", label: "Increase in staff productivity", icon: "TrendingUp" },
      { value: "$0", label: "Added payroll — no hiring or training", icon: "PiggyBank" },
    ],
  },
  services: {
    title: "What we handle for your practice",
    items: [
      { icon: "ShieldCheck", title: "Full Insurance Verification", body: "Eligibility, benefits, deductibles, maximums, frequency limitations, waiting periods and downgrades — verified and documented before the patient sits down." },
      { icon: "ClipboardCheck", title: "Prior Authorizations", body: "We identify, submit and track authorizations for major and ortho cases so treatment starts on time and revenue is protected." },
      { icon: "FileText", title: "Claims Filing & Follow-Up", body: "Clean claim submission and consistent carrier follow-up to accelerate payments and keep aging A/R under control." },
      { icon: "Calculator", title: "Treatment & Ortho Estimates", body: "Accurate patient portions and ortho contract breakdowns so your team presents confident, transparent financials." },
      { icon: "Users", title: "Patient Insurance Communication", body: "Clear breakdowns of co-pays, coverage limits and out-of-pocket costs that improve case acceptance and reduce no-shows." },
      { icon: "Building2", title: "DSO & Multi-Location Support", body: "Standardized insurance workflows across every location for consistent, comparable performance." },
    ],
  },
  ortho: {
    title: "Built for the way orthodontic practices bill",
    body: "Orthodontic billing is different — continuation of treatment, initial banding vs. monthly installments, lifetime maximums and coordination of benefits all create room for error. We manage ortho-specific verification and contract logic so your case acceptance and monthly production stay protected.",
    bullets: [
      "Initial + monthly installment contract verification",
      "Lifetime ortho maximum & age-limit checks",
      "Continuation-of-treatment coordination on transfers",
      "Coordination of benefits for dual coverage",
      "Autopay & recurring claim submission tracking",
    ],
    image: "bracesExam",
  },
  cta: {
    title: "See what your practice could save",
    body: "Request a free Insurance Revenue Risk Audit. We'll review your verification and claims workflows and show you exactly where revenue is slipping through the cracks — no obligation.",
  },
};

export const hospitals = {
  hero: {
    eyebrow: "Answering Service for Hospitals",
    title: "24/7 HIPAA-Compliant Healthcare Call Center",
    subtitle:
      "Exceptional live answering, messaging and medical practice support around the clock — so your staff can focus on care instead of the phones.",
    image: "hospitalCorridor",
  },
  features: [
    { icon: "Clock", title: "24/7 Support", body: "We're always here — day or night — so calls are never mishandled or unanswered at any facility." },
    { icon: "UserCheck", title: "Expert Agents", body: "Highly trained, knowledgeable agents who understand the ins and outs of hospital structure and operations." },
    { icon: "Lock", title: "HIPAA Compliant", body: "Comprehensive phone, email and live-chat support adhering to strict HIPAA regulations for patient privacy." },
    { icon: "Settings", title: "Customizable Solutions", body: "Tailored plans that address the unique needs of your facility, departments and patients." },
    { icon: "BarChart3", title: "Data-Driven Insights", body: "Advanced analytics into caller behavior to help you make informed operational decisions." },
    { icon: "Scaling", title: "Scalable Services", body: "Quickly scale coverage up or down so patients always receive the support they need." },
  ],
  expect: {
    title: "What you can expect from us",
    items: [
      "A team of highly-trained, professional agents who put callers at ease and get them the assistance they need",
      "Greatly increased availability for staff to give care to patients",
      "A dedicated team who understands hospital structure and operations",
      "Full access to our call-management systems and tracking tools",
      "24/7 call answering — so calls are never mishandled or unanswered",
      "100% HIPAA compliance across all solutions, staff and tools",
    ],
  },
  detail: {
    title: "Give time back to your internal staff",
    body: "One of the most important benefits of a hospital answering service is the time it gives back to internal staff. Doctors focus on treatment without unnecessary phone interruptions, and front-desk staff focus on the people in front of them. We route time-sensitive calls directly to hospital personnel and handle smaller issues with a team of highly trained agents.",
    image: "hospitalTeam",
  },
};

export const insuranceProviders = {
  hero: {
    eyebrow: "End-to-End Claims Cycle Management",
    title: "Insurance Claims Cycle Management",
    subtitle:
      "Let VerifiAssist manage your insurance claims and streamline your claims cycle to save time and money — reducing operations cost through effective outsourcing.",
    image: "businessTeam",
  },
  benefits: [
    "High-quality customer service despite volume fluctuation",
    "Greater customer satisfaction & retention",
    "Greater accuracy in settlement of claims",
    "Fraudulent claims management",
    "Reduction in operations cost",
  ],
  services: [
    { icon: "FileInput", title: "Claims FNOL", body: "Fast, accurate first-notice-of-loss intake that starts every claim on the right foot." },
    { icon: "FolderKanban", title: "Claims Case Management", body: "Track claim payments to providers and claimants with authorization-based processing and reinsurance/coinsurance management." },
    { icon: "Split", title: "Claims Allocation", body: "Manual and automatic allocation based on business rules, with workload management and network partner tracking." },
    { icon: "ScanLine", title: "Claims Document Management", body: "Digital document repository with marking, digital signatures, document numbering and portal integration for agents." },
    { icon: "ShieldAlert", title: "Risk Management", body: "Fraud detection from input data, with alerts for litigation, possible fraud, fatality and other key escalation events." },
    { icon: "CheckCircle2", title: "Claims Validation", body: "Integrated policy and coverage verification, fast-track determination, decision support and claim history review." },
  ],
  why: {
    title: "Why outsource your claims cycle to us?",
    items: [
      { icon: "Workflow", title: "Streamlined Processes", body: "Purpose-built claims workflows that reduce handling time and errors." },
      { icon: "GraduationCap", title: "Trained Insurance Agents", body: "Experienced agents in customer services and claims management." },
      { icon: "Smile", title: "Increased Satisfaction", body: "Better customer experience drives loyalty and retention." },
      { icon: "DollarSign", title: "Affordable Rates", body: "Reduce operations cost by up to 65% per year." },
      { icon: "Timer", title: "Quick Turnaround", body: "Skilled teams settle claims faster and keep the cycle moving." },
      { icon: "Lock", title: "Data Security", body: "Secure, authorization-based processing protects sensitive data." },
    ],
  },
};

export const arCollections = {
  hero: {
    eyebrow: "AR & Debt Collections",
    title: "Stop Revenue Leakage from Aging Accounts Receivable",
    subtitle:
      "We follow up, track and recover your unpaid invoices — so your team can focus on customers, not chasing payments.",
    image: "businessTeam2",
  },
  intro: [
    "We help businesses recover lost revenue tied up in aging accounts receivable. Our team combines proven outreach processes, real-time tracking and experienced agents to turn overdue invoices into cash — without adding overhead to your team.",
    "With full transparency and performance-driven results, we align our success with yours. Our remote support model integrates seamlessly with your existing systems, giving you structured, consistent AR follow-up without the cost of full-time staff.",
  ],
  stats: [
    { value: "20–40%", label: "Of aging receivables recovered", icon: "TrendingUp" },
    { value: "40%", label: "Reduction in overdue invoices", icon: "TrendingDown" },
    { value: "$0", label: "Added payroll overhead", icon: "PiggyBank" },
  ],
  helps: [
    "Reduce outstanding accounts receivable",
    "Accelerate cash flow through structured payment follow-up",
    "Recover unpaid invoices before they become write-offs",
    "Maintain professional communication while collecting balances",
    "Eliminate time spent chasing payments without increasing payroll",
  ],
  segments: [
    { icon: "Wrench", title: "Service-Based Businesses", body: "HVAC, plumbing, electrical, contractors, auto repair and recurring service businesses." },
    { icon: "Briefcase", title: "Professional Services", body: "Marketing agencies, consultants, MSPs and accounting & bookkeeping firms." },
    { icon: "Stethoscope", title: "Healthcare Practices", body: "Dental, orthodontic and medical practices with patient and insurance balances." },
  ],
};

export const hipaa = {
  hero: {
    eyebrow: "HIPAA Privacy Policies",
    title: "How We Protect Patient & Insurance Data",
    subtitle:
      "Data security matters. Our team is trained on HIPAA-conscious processes and follows secure workflows designed to protect sensitive information in every interaction.",
  },
  sections: [
    { title: "Our Commitment", body: "VerifiAssist prioritizes accuracy, confidentiality and professionalism in every interaction. We treat all patient and insurance data as protected health information (PHI) and handle it with strict care." },
    { title: "Secure Workflows", body: "All communication and data-handling processes are built with security in mind — from verification calls and emails to eFax and reporting. Access is limited to trained, authorized agents." },
    { title: "Agent Training", body: "Our agents receive regular training on HIPAA-conscious processes, secure data handling and quality assurance so that every verification is completed thoroughly and reliably." },
    { title: "Quality Assurance", body: "A rigorous QA process reviews interactions for compliance and accuracy, helping identify and correct any process gaps quickly." },
    { title: "Confident Outsourcing", body: "Because our processes are built for security and consistency, your practice can outsource verification and claims support without adding risk." },
  ],
  points: [
    "HIPAA-conscious workflows on every interaction",
    "Access limited to trained, authorized agents",
    "Secure handling of calls, email and eFax",
    "Regular agent training & quality assurance",
    "Transparent reporting and accountability",
  ],
};

export const contact = {
  hero: {
    eyebrow: "Contact Us",
    title: "Ready to Protect Your Revenue?",
    subtitle:
      "Let's take a structured look at your verification and claims workflows to identify preventable denial risk and reimbursement delays. Request your free Insurance Revenue Risk Audit below.",
  },
  challenges: [
    "Higher denial rate",
    "Delayed reimbursements",
    "Staffing overload",
    "Insurance write-offs",
    "Aging accounts receivable",
    "Other",
  ],
};

export const pricing = {
  hero: {
    eyebrow: "Pricing",
    title: "Simple, Flat-Rate Plans — Sized to Your Practice",
    subtitle:
      "A dedicated remote team built for dental and orthodontic practices — not a shared call center. Predictable monthly pricing, no long-term lock-in, and no hiring, benefits or turnover risk on you.",
  },
  cost: {
    title: "What admin overload is quietly costing you",
    subtitle: "It isn't a you problem — it's an industry-wide leak. Here are the national averages.",
    stats: [
      { value: "15%", label: "of dental insurance claims are denied on average — rework that eats staff hours" },
      { value: "16%", label: "of billed revenue goes uncollected — the gap between billed and actually paid" },
      { value: "~20%", label: "of scheduled appointments are lost to cancellations and no-shows" },
    ],
    note: "Sources: dental insurance industry claim & collections benchmarks, 2025–2026.",
  },
  plans: [
    {
      name: "Growth",
      price: "$1,650",
      period: "/month",
      tagline: "Most single-location practices",
      highlight: false,
      cta: "Start with Growth",
      summary: "Full-time dedicated rep — 1 agent",
      features: [
        "1 full-time dedicated agent",
        "40 hours/week · 160 hours/month",
        "Works your standard business hours",
        "Up to 800 insurance verification cases/month",
        "Coverage checks, eligibility & benefit breakdowns",
        "Dedicated account management",
      ],
    },
    {
      name: "Full Coverage",
      price: "$3,000",
      period: "/month",
      tagline: "Multi-provider or multi-location",
      highlight: true,
      cta: "Choose Full Coverage",
      summary: "Multi-role team — 2 agents",
      features: [
        "2 full-time dedicated agents",
        "40 hours/week · 160 hours/month per agent",
        "Insurance verification + one added task",
        "Billing, scheduling or patient follow-up",
        "Up to 1,600 insurance verification cases/month",
        "Priority account management & performance reviews",
      ],
    },
    {
      name: "Enterprise / DSO",
      price: "Custom",
      period: "",
      tagline: "Multi-location groups & DSOs",
      highlight: false,
      cta: "Talk to us",
      summary: "Scaled team across every location",
      features: [
        "3+ dedicated agents, scaled to volume",
        "Standardized workflows across all locations",
        "Full revenue-cycle support (verification → collections)",
        "Consolidated, comparable reporting per location",
        "Custom case volume & SLAs",
        "Senior account strategist",
      ],
    },
  ],
  note: "Final pricing and service scope are confirmed on your discovery call, based on your call and patient volume.",
  includedTitle: "Every plan includes",
  included: [
    { icon: "UserCheck", title: "A dedicated team", body: "Trained specifically for your practice — not a shared pool or a chatbot." },
    { icon: "ShieldCheck", title: "HIPAA-conscious workflows", body: "Secure data handling on every verification and interaction." },
    { icon: "Plug", title: "Works in your systems", body: "We integrate into your existing PMS and clearinghouse — no new software." },
    { icon: "LineChart", title: "Transparent reporting", body: "Regular performance check-ins so you always see the impact." },
    { icon: "CalendarClock", title: "Launch in days", body: "A guided onboarding gets your team live in days, not months." },
    { icon: "Wallet", title: "Predictable flat cost", body: "One monthly rate — no hiring, benefits or turnover risk." },
  ],
  compare: {
    title: "Why practices choose VerifiAssist",
    columns: ["In-House Hire", "Generic BPO", "VerifiAssist"],
    rows: [
      { label: "Dedicated to your practice", values: [true, "Rarely", true] },
      { label: "Trained on dental/ortho workflows", values: ["Varies", "Rarely", true] },
      { label: "Hiring, benefits & turnover risk", values: ["On you", "N/A", "Handled for you"] },
      { label: "Predictable flat monthly cost", values: [false, "Sometimes", true] },
      { label: "Time to launch", values: ["Weeks–months", "Days", "Days"] },
    ],
  },
  steps: {
    title: "From first call to a live team",
    items: [
      { n: "01", title: "Discovery Call", body: "We learn your systems, workflows and biggest pain points." },
      { n: "02", title: "Team Match & Training", body: "We hand-pick and train a team specifically for your practice." },
      { n: "03", title: "Launch", body: "Your team goes live, integrated into your existing systems." },
      { n: "04", title: "Ongoing Oversight", body: "Dedicated account management and regular performance check-ins." },
    ],
  },
  faqs: [
    { q: "Are there long-term contracts?", a: "No long-term lock-in. Plans are month-to-month and pricing is confirmed based on your call and patient volume." },
    { q: "What if we go over our case volume?", a: "We'll flag it early and right-size your plan together — you're never surprised by a bill." },
    { q: "Can we start with verification only and add more later?", a: "Absolutely. Insurance verification is our specialty; scheduling, follow-up and billing can be added whenever you're ready." },
    { q: "Do we handle hiring or management?", a: "No. We recruit, train, manage and cover turnover. You get the output and reporting without the payroll overhead." },
  ],
  caseStudies: {
    title: "Real results from practices like yours",
    subtitle: "Representative before/after outcomes from practices using VerifiAssist. Metrics reflect typical ranges — your results are confirmed during your audit.",
    disclaimer: "Illustrative examples based on typical client outcomes. Swap in your own verified metrics once live.",
    items: [
      {
        practice: "3-Location Dental Group",
        plan: "Full Coverage",
        summary:
          "Front desk was drowning in payer hold times and rework. We took over verification and claim status tracking across all three locations.",
        metrics: [
          { label: "Claim denials", before: "14%", after: "5%", improve: "-64%" },
          { label: "Front-desk hours/week on insurance", before: "22 hrs", after: "6 hrs", improve: "-16 hrs" },
          { label: "Clean verifications before visit", before: "68%", after: "98%", improve: "+30 pts" },
        ],
      },
      {
        practice: "Single-Location Orthodontic Practice",
        plan: "Growth",
        summary:
          "Ortho contract errors and missed authorizations were stalling case starts. Dedicated verification protected case acceptance and monthly production.",
        metrics: [
          { label: "Case starts delayed by insurance", before: "1 in 5", after: "1 in 25", improve: "-80%" },
          { label: "Days to reimbursement", before: "41 days", after: "24 days", improve: "-17 days" },
          { label: "Front-desk overtime", before: "Weekly", after: "Rare", improve: "Eliminated" },
        ],
      },
      {
        practice: "Multi-Provider Dental & Medical",
        plan: "Enterprise / DSO",
        summary:
          "Aging A/R and inconsistent workflows across providers. We standardized verification and added billing follow-up to recover stalled claims.",
        metrics: [
          { label: "Aging A/R (90+ days)", before: "$186k", after: "$104k", improve: "-44%" },
          { label: "Uncollected billed revenue", before: "16%", after: "7%", improve: "-9 pts" },
          { label: "No-show rate", before: "19%", after: "11%", improve: "-8 pts" },
        ],
      },
    ],
  },
};

// Mock submit helper — stores leads in localStorage until backend is connected.
export const saveLeadMock = (lead) => {
  try {
    const key = "verifiassist_leads";
    const existing = JSON.parse(localStorage.getItem(key) || "[]");
    existing.push({ ...lead, id: Date.now(), createdAt: new Date().toISOString() });
    localStorage.setItem(key, JSON.stringify(existing));
    return true;
  } catch (e) {
    return false;
  }
};
